import { defineStore } from "pinia";
import { constants } from "@/constants";
import { useCartStore } from "@/store/cart";
import { useSessionStore } from "@/store/session";
import MultiLocalStorage from "@/services/multiLocalStorage";
import CartService from "@/services/cart";
import Redirect from "@/services/redirect";
import { isVanguardOnlyExtrasellSession } from "@/services/confirmationPreview";

const isInitialOrderType = (orderType) => !orderType || orderType === "initial";

const ORDER_PAGE_SUFFIXES = ["/confirmation", "/waiting"];

const normalizePath = (path) => {
  if (!path || path === "/") {
    return "/";
  }

  return path.endsWith("/") ? path.slice(0, -1) || "/" : path;
};

const getOrderPageBasePath = (path) => {
  const pathname = normalizePath(path);

  for (const suffix of ORDER_PAGE_SUFFIXES) {
    if (pathname !== suffix && pathname.endsWith(suffix)) {
      return normalizePath(pathname.slice(0, -suffix.length));
    }
  }

  return pathname;
};

const isScopedOrderPageContext = () => {
  const pathname = normalizePath(window?.location?.pathname || "/");
  const searchParams = new URLSearchParams(window?.location?.search || "");

  if (window?.globalConfig?.isOrderPage) {
    return true;
  }

  if (searchParams.has("retry")) {
    return true;
  }

  return (
    pathname !== "/confirmation" &&
    pathname !== "/waiting" &&
    ORDER_PAGE_SUFFIXES.some((suffix) => pathname.endsWith(suffix))
  );
};

const getCurrentOrderPageBasePath = () => {
  return getOrderPageBasePath(window?.location?.pathname || "/");
};

const getHistoryItemBasePath = (historyItem) => {
  const orderUrl = historyItem?.order?.url;

  if (!orderUrl) {
    return null;
  }

  try {
    return getOrderPageBasePath(
      new URL(orderUrl, window.location.origin).pathname,
    );
  } catch (error) {
    return null;
  }
};

const scopeOrderHistoryForCurrentPage = (history) => {
  if (!Array.isArray(history)) {
    return [];
  }

  if (!isScopedOrderPageContext()) {
    return history;
  }

  const currentBasePath = getCurrentOrderPageBasePath();

  return history.filter((historyItem) => {
    return getHistoryItemBasePath(historyItem) === currentBasePath;
  });
};

const getHistoryChainUpToOrder = (history, currentOrderId) => {
  if (!currentOrderId || !Array.isArray(history) || history.length === 0) {
    return [];
  }

  const selectedIndex = history.findIndex((historyItem) => {
    return historyItem.order?.externalOrderId === currentOrderId;
  });

  if (selectedIndex === -1) {
    return [];
  }

  let initialIndex = selectedIndex;
  while (initialIndex >= 0) {
    if (history[initialIndex].order?.orderType === "initial") {
      break;
    }
    initialIndex -= 1;
  }

  const startIndex = initialIndex >= 0 ? initialIndex : selectedIndex;
  let endIndex = selectedIndex;
  if (isInitialOrderType(history[selectedIndex].order?.orderType)) {
    for (let i = selectedIndex + 1; i < history.length; i += 1) {
      const orderType = history[i].order?.orderType;
      if (orderType === "upsell" || orderType === "crosssell") {
        endIndex = i;
        continue;
      }
      break;
    }
  }
  return history.slice(startIndex, endIndex + 1);
};

const getLatestSessionHistory = (history) => {
  if (!Array.isArray(history) || history.length === 0) {
    return [];
  }

  let startIndex = history.length - 1;
  while (startIndex > 0 && history[startIndex].order?.orderType !== "initial") {
    startIndex -= 1;
  }

  if (history[startIndex].order?.orderType === "initial") {
    return history.slice(startIndex);
  }

  return history.slice(-1);
};

const resolveConfirmationOrderId = (scopedHistory, paramPlusOrderId) => {
  if (!Array.isArray(scopedHistory) || scopedHistory.length === 0) {
    return null;
  }

  const latestEntry = scopedHistory[scopedHistory.length - 1];
  const latestOrderId = latestEntry?.order?.externalOrderId || null;

  if (!paramPlusOrderId) {
    return latestOrderId;
  }

  const paramPlusIndex = scopedHistory.findIndex(
    (historyItem) => historyItem.order?.externalOrderId === paramPlusOrderId,
  );

  if (paramPlusIndex === -1) {
    return latestOrderId;
  }

  // PARAM_PLUS can still reference a previous attempt on the same order page
  // (e.g. classic checkout before a 1-click SEPA retry).
  if (
    paramPlusIndex < scopedHistory.length - 1 &&
    latestEntry.action === "order" &&
    isInitialOrderType(latestEntry.order?.orderType)
  ) {
    return latestOrderId;
  }

  return paramPlusOrderId;
};

export const useOrderHistoryStore = defineStore("orderHistory", {
  state: () => ({
    _lastUpdate: null,
    _orderHistory: [],
    _currentStepType: null,
  }),
  getters: {
    orderHistory: (state) => {
      MultiLocalStorage.checkCleanOrderHistory();

      if (state._orderHistory.length === 0) {
        const orderHistory = MultiLocalStorage.get(
          constants.ECOM_ORDER_HISTORY,
          window?.globalConfig?.domainId,
        );
        return orderHistory ? orderHistory : [];
      }

      return state._orderHistory;
    },
    currentPageOrderHistory() {
      return scopeOrderHistoryForCurrentPage(this.orderHistory);
    },
    activeOrderHistory() {
      const scoped = this.currentPageOrderHistory;
      if (scoped.length === 0) {
        return [];
      }

      const currentOrderId = resolveConfirmationOrderId(
        scoped,
        Redirect.getParamPlusData()?.orderId || null,
      );
      if (currentOrderId) {
        const chain = getHistoryChainUpToOrder(scoped, currentOrderId);
        if (chain.length > 0) {
          return chain;
        }
      }

      return getLatestSessionHistory(scoped);
    },
    lastOrderHistoryItem() {
      return this.currentPageOrderHistory.slice(-1)[0] || null;
    },
    activeLastOrderHistoryItem() {
      const history = this.activeOrderHistory;
      return history[history.length - 1] || null;
    },
    lastUpdate: (state) => state._lastUpdate,
    currentStepType: (state) => state._currentStepType,
  },
  actions: {
    setCurrentStepType(stepType) {
      this._currentStepType = stepType;
    },
    addStepToHistory(skippedStep = null) {
      MultiLocalStorage.checkCleanOrderHistory();

      if (this.orderHistory.length > 0) {
        const lastHistoryItem = this.orderHistory[this.orderHistory.length - 1];
        const current = Number(lastHistoryItem.step);
        const fromHistory = Number.isFinite(current) ? current + 1 : 1;
        const skipped = Number(skippedStep);
        const fromSkip = Number.isFinite(skipped) ? skipped + 1 : fromHistory;
        lastHistoryItem.step = Math.max(fromHistory, fromSkip);
        lastHistoryItem.action = "skip";
        if (this._currentStepType) {
          lastHistoryItem.stepType = this._currentStepType;
        }
      }

      this._orderHistory = this.orderHistory;

      MultiLocalStorage.set(
        constants.ECOM_ORDER_HISTORY,
        window?.globalConfig?.domainId,
        this.orderHistory,
      );

      this._lastUpdate = new Date();
    },
    addOrderToHistory(cart, order, step) {
      MultiLocalStorage.checkCleanOrderHistory();

      if (!order.sessionIdentifier) {
        order.sessionIdentifier = useSessionStore().session.identifier;
      }

      const existingHistoryItem = this.orderHistory.find(
        (i) => i.order.externalOrderId === order.externalOrderId,
      );

      const minifiedCart = CartService.minifyCartForHistory(cart);

      const entry = { order, cart: minifiedCart, step, action: "order" };
      if (this._currentStepType) {
        entry.stepType = this._currentStepType;
      }

      if (!existingHistoryItem) {
        this.orderHistory.push(entry);
      } else {
        Object.assign(existingHistoryItem, entry);
      }

      this.orderHistory.splice(0, this.orderHistory.length - 4); // limiting history to the 4 last orders

      this._orderHistory = this.orderHistory;

      MultiLocalStorage.set(
        constants.ECOM_ORDER_HISTORY,
        window?.globalConfig?.domainId,
        this.orderHistory,
      );

      this._lastUpdate = new Date();
    },
    resetForOrderPage(currentOrderId = null) {
      const history = this.currentPageOrderHistory;
      if (history.length === 0) return;

      // Snapshot before any mutation (getLatestSessionHistory anchors are
      // mutated in place below) so the "did it actually change" comparison
      // reflects the pre-call state, not an already-mutated reference.
      const previousHistorySnapshot = JSON.stringify(this.orderHistory);

      let nextHistory = getHistoryChainUpToOrder(history, currentOrderId);

      if (nextHistory.length === 0) {
        nextHistory = getLatestSessionHistory(history);
        if (nextHistory.length > 0) {
          const anchor = nextHistory[0];
          anchor.step = 0;
          delete anchor.action;
        }
      }

      const currentPageHistoryIds = new Set(
        history.map((historyItem) => historyItem.order?.externalOrderId),
      );
      const preservedHistory = this.orderHistory.filter((historyItem) => {
        return !currentPageHistoryIds.has(historyItem.order?.externalOrderId);
      });

      const nextOrderHistory = [...preservedHistory, ...nextHistory];

      // Avoid bumping _lastUpdate (and rewriting localStorage) when the
      // resulting history is identical to before: callers like
      // ensureCurrentOrderScope run on every mount/route check, and an
      // unconditional bump here re-triggers every historyLastUpdate watcher
      // (including the confirmation /extrasell fetch) without a real reason.
      if (JSON.stringify(nextOrderHistory) === previousHistorySnapshot) {
        return;
      }

      this._orderHistory = nextOrderHistory;
      MultiLocalStorage.set(
        constants.ECOM_ORDER_HISTORY,
        window?.globalConfig?.domainId,
        this._orderHistory,
      );
      this._lastUpdate = new Date();
    },
    ensureCurrentOrderScope() {
      if (!isScopedOrderPageContext()) {
        return;
      }
      if (isVanguardOnlyExtrasellSession()) {
        return;
      }

      const scoped = this.currentPageOrderHistory;
      const paramPlusData = Redirect.getParamPlusData() || {};
      const resolvedOrderId = resolveConfirmationOrderId(
        scoped,
        paramPlusData.orderId || null,
      );

      if (resolvedOrderId && resolvedOrderId !== paramPlusData.orderId) {
        const resolvedOrder = scoped.find(
          (historyItem) =>
            historyItem.order?.externalOrderId === resolvedOrderId,
        )?.order;

        localStorage.setItem(
          constants.PARAM_PLUS,
          JSON.stringify({
            ...paramPlusData,
            orderId: resolvedOrderId,
            paymentType:
              resolvedOrder?.paymentType || paramPlusData.paymentType || null,
          }),
        );
      }

      this.resetForOrderPage(resolvedOrderId);
    },
    removeRetryOrders() {
      MultiLocalStorage.checkCleanOrderHistory();

      if (this.orderHistory.length > 0) {
        this._orderHistory = this.orderHistory.filter((i) => !i.order.isRetry);
      }

      MultiLocalStorage.set(
        constants.ECOM_ORDER_HISTORY,
        window?.globalConfig?.domainId,
        this.orderHistory,
      );

      this._lastUpdate = new Date();
    },
    setBillingSubscriberKey(subscriberKey) {
      MultiLocalStorage.checkCleanOrderHistory();

      const scopedOrderIds = new Set(
        scopeOrderHistoryForCurrentPage(this.orderHistory).map(
          (historyItem) => historyItem.order?.externalOrderId,
        ),
      );

      for (const historyItem of this.orderHistory) {
        if (
          scopedOrderIds.has(historyItem.order?.externalOrderId) &&
          historyItem.order
        ) {
          historyItem.order.billingSubscriberKey = subscriberKey;
        }
      }

      this._orderHistory = [...this.orderHistory];

      MultiLocalStorage.set(
        constants.ECOM_ORDER_HISTORY,
        window?.globalConfig?.domainId,
        this.orderHistory,
      );

      this._lastUpdate = new Date();
    },
  },
});
